import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Label from '../../global/formControls/Label';
import Select from '../../global/formControls/Select';
import Input from '../../global/formControls/Input';
import Button from '../../global/Button';
import P from '../../global/typography/P';

const placeholder = 'Select an option';
const postalCodeRegex = /^\d{5}$/;
const unitedStatesId = '0';
const rangeOptions = [
  {
    id: 10,
    name: '10 miles'
  },
  {
    id: 20,
    name: '20 miles'
  },
  {
    id: 50,
    name: '50 miles'
  },
  {
    id: 100,
    name: '100 miles'
  },
  {
    id: 150,
    name: '150 miles'
  }
];

const FormGroup = styled.div`
  margin: 12px 0;
`;

const FormWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;

  ${FormGroup} {
    width: 48%;

    :nth-last-child(1):nth-child(odd)  {
      width: 100%;
    }
  }
`;

const WtbLabel = styled(Label)`
  margin-bottom: 5px;
`;

const ValidaitonMessage = styled.div`
  font-weight: ${props => props.theme.fontWeight.bold};
  font-style: italic;
  margin-bottom: 23px;
`;

class SearchForm extends Component {
  state = {
    internationalLocation: unitedStatesId,
    postalCode: '',
    postalCodeIsInvalid: true,
    product: '',
    productOptions: [],
    range: 20,
    role: '',
    searchType: 'us',
    training: '',
    trainingOptions: [{ id: null, name: 'Clear selection' }].concat(
      this.props.training.map(t => ({ id: t, name: t }))
    )
  };

  isSearchDisabled = () => {
    const {
      internationalLocation,
      postalCodeIsInvalid,
      product,
      role,
      searchType
    } = this.state;

    if (searchType === 'other') {
      return !internationalLocation;
    }

    return postalCodeIsInvalid || !role.id || !product.id;
  };

  runSearch = () => {
    const { onSearch } = this.props;
    const { internationalLocation, role, searchType } = this.state;

    if (searchType === 'other') {
      onSearch(
        {
          isInternational: true,
          variables: { id: internationalLocation.id }
        },
        'Sales Rep'
      );
      return;
    }

    const type = (x =>
      ({
        'Service Contractor': 'contractor',
        Distributor: 'distributor',
        'Factory Rep': 'salesRep'
      }[x]))(role.name);

    if (this.state.training.id === null) {
      this.setState({
        training: null
      });
    }

    onSearch(
      {
        isInternational: false,
        variables: {
          filters: [this.state.product.id],
          postalCode: this.state.postalCode,
          range: this.state.range,
          training:
            type === 'contractor' && this.state.training
              ? this.state.training.id
              : null,
          type
        }
      },
      role.name
    );
  };

  selectInternationalLocation = internationalLocation => {
    this.setState({
      internationalLocation,
      searchType: internationalLocation.id === unitedStatesId ? 'us' : 'other'
    });
    this.props.onSearchTypeChange(internationalLocation);
  };

  selectProduct = val => {
    this.setState({ product: val });
  };

  selectRole = val => {
    this.setState({
      productOptions: val.filters,
      role: val,
      training: ''
    });
  };

  selectTraining = val => this.setState({ training: val });

  updatepostalCode = e => {
    this.updateProp('postalCode', e);
    this.validatePostalCode(e.target.value);
  };

  updateProp = (prop, event) => {
    this.setState({
      [prop]: event.target.value
    });
  };

  updateRange = range => this.setState({ range: range.id });

  validatePostalCode = postalCode => {
    const postalCodeIsInvalid = !postalCodeRegex.test(postalCode);

    this.setState({
      postalCodeIsInvalid
    });

    return !postalCodeIsInvalid;
  };

  render() {
    const { searchType } = this.state;

    return (
      <div>
        <FormGroup>
          <WtbLabel>Where</WtbLabel>
          <Select
            labelKey="name"
            placeholder="Select a Country"
            onChange={this.selectInternationalLocation}
            options={this.props.internationalLocations}
            value={this.state.internationalLocation}
            valueKey="id"
          />
        </FormGroup>
        {searchType === 'us' && (
          <React.Fragment>
            <FormWrapper>
              <FormGroup>
                <WtbLabel invalid={this.state.postalCodeIsInvalid}>Zip Code*</WtbLabel>
                <Input
                  invalid={this.state.postalCodeIsInvalid}
                  onChange={this.updatepostalCode}
                  onBlur={e => this.validatePostalCode(e.target.value)}
                  placeholder="e.g., 37090"
                  value={this.state.postalCode}
                />
              </FormGroup>
              {this.state.role.name !== 'Factory Rep' && (
                <FormGroup>
                  <WtbLabel>Within</WtbLabel>
                  <Select
                    labelKey="name"
                    onChange={this.updateRange}
                    options={rangeOptions}
                    value={this.state.range}
                    valueKey="id"
                  />
                </FormGroup>
              )}
              <FormGroup>
                <WtbLabel invalid={!this.state.role.id}>Who*</WtbLabel>
                <Select
                  labelKey="name"
                  invalid={!this.state.product.id}
                  placeholder={placeholder}
                  onChange={this.selectRole}
                  options={this.props.roleOptions}
                  value={this.state.role}
                  valueKey="id"
                />
              </FormGroup>
              <FormGroup>
                <WtbLabel invalid={!this.state.product.id}>Product*</WtbLabel>
                <Select
                  labelKey="name"
                  invalid={!this.state.product.id}
                  placeholder={placeholder}
                  onChange={this.selectProduct}
                  options={this.state.productOptions}
                  value={this.state.product}
                  valueKey="id"
                />
              </FormGroup>
              {this.state.role.name === 'Service Contractor' && (
                <FormGroup className="wtbFullWidth">
                  <WtbLabel>Training Completed</WtbLabel>
                  <Select
                    labelKey="name"
                    placeholder={placeholder}
                    onChange={this.selectTraining}
                    options={this.state.trainingOptions}
                    value={this.state.training}
                    valueKey="id"
                  />
                </FormGroup>
              )}
            </FormWrapper>
          </React.Fragment>
        )}
        {this.isSearchDisabled() && (
          <ValidaitonMessage>
            <P>*Please complete the required fields to continue.</P>
          </ValidaitonMessage>
        )}
        <Button
          disabled={this.isSearchDisabled()}
          full
          onClick={this.runSearch}
        >
          Search
        </Button>
      </div>
    );
  }
}

SearchForm.propTypes = {
  internationalLocations: PropTypes.array,
  onSearch: PropTypes.func,
  onSearchTypeChange: PropTypes.func,
  roleOptions: PropTypes.array,
  training: PropTypes.array
};

SearchForm.defaultProps = {
  internationalLocations: [],
  onSearch: () => {},
  onSearchTypeChange: () => {}
};

export default SearchForm;
